import React from 'react';
import { FieldValue } from '../../organisms';

const isNativeFormElement = (element: React.ReactElement): boolean => {
  const type = element.type;
  if (typeof type === 'string') {
    return type === 'input' || type === 'textarea';
  }
  return false;
};

const isTextComponent = (element: React.ReactElement): boolean => {
  const type = element.type;
  if (typeof type === 'function' && type.name === 'Text') {
    return true;
  }
  return false;
};

export const injectPropsToMatchingFields = (
  node: React.ReactNode,
  propsToInject: {
    id: string;
    name: string;
    value: any;
    onValueChange: (value: FieldValue) => void;
    disabled?: boolean;
    invalid?: boolean;
    required?: boolean;
  },
  shouldInjectValueProps: boolean,
): React.ReactNode => {
  // Handle arrays of children at the top level
  if (React.Children.count(node) > 1 || Array.isArray(node)) {
    return React.Children.map(node, (child) =>
      injectPropsToMatchingFields(child, propsToInject, shouldInjectValueProps)
    );
  }

  // Handle null, undefined, or primitive nodes
  if (!React.isValidElement(node)) {
    return node;
  }

  const originalOnValueChange = node.props?.onValueChange;
  const originalOnChange = node.props?.onChange;

  const updatedOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    propsToInject.onValueChange(newValue);
    if (originalOnChange) {
      originalOnChange(e);
    }
  };

  const injectedProps: Record<string, any> = {
    id: propsToInject.id,
    disabled: propsToInject.disabled,
    invalid: propsToInject.invalid,
    required: propsToInject.required,
  };

  if (shouldInjectValueProps) {
    injectedProps.value = propsToInject.value;
    injectedProps.onValueChange = (value: FieldValue) => {
      propsToInject.onValueChange(value);
      if (originalOnValueChange) {
        originalOnValueChange(value);
      }
    };
  }

  if (isTextComponent(node)) {
    const children = node.props.children;
    const newChildren = React.Children.map(children, (child) =>
      injectPropsToMatchingFields(child, propsToInject, shouldInjectValueProps)
    );
    return React.cloneElement(node, {
      ...node.props,
      children: newChildren,
    });
  }

  if (isNativeFormElement(node)) {
    return React.cloneElement(node, {
      ...node.props,
      ...injectedProps,
      onChange: shouldInjectValueProps ? updatedOnChange : originalOnChange,
    });
  }

  // Always process children if they exist
  if (typeof node.type === 'string' && node.props.children) {
    const children = node.props.children;
    const newChildren = React.Children.map(children, (child) =>
      injectPropsToMatchingFields(child, propsToInject, shouldInjectValueProps)
    );

    // For form elements or other components, inject props and include processed children
    return React.cloneElement(node, {
      ...node.props,
      children: newChildren,
    });
  }

  return React.cloneElement(node, {
    ...node.props,
    ...injectedProps,
  });
}
